import { css } from '@emotion/react';
import { Typography } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useMemo } from 'react';
import { DataCard } from '../../../../components/Card/DataCard';
import { OwnershipTimelineStack } from '../../../../schemas/OwnershipTimeline.schema';
import { colors } from '../../../../theme/colors';
import { FMT } from '../../../../util/formatter-service';
import { useShowSummaryV2 } from '../useShowSummaryV2';
import { useHoldingsAsOfDate } from './state';

const list = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0 0.5rem 0.5rem;
`;

const returnChip = css`
  align-items: center;
  display: inline-flex;
  gap: 0.125rem;
  white-space: nowrap;
`;

interface IHoldingsKpiCardsProps {
  companyId: number;
}

export function HoldingsKpiCards({ companyId }: IHoldingsKpiCardsProps) {
  const showSummaryV2 = useShowSummaryV2();
  const holdings = useHoldingsAsOfDate(companyId);
  const totals = useMemo(() => aggregate(holdings), [holdings]);

  if (!showSummaryV2) {
    return (
      <ul css={list}>
        <DataCard title='Current Ownership' formattedValue={FMT.format('percent', totals.ownershipPct)} />
        <DataCard title='Shares Outstanding' formattedValue={FMT.format('numericShort', totals.shares)} />
        <DataCard title='Total Invested' formattedValue={FMT.format('currencyShort', totals.invested)} />
        <DataCard title='FMV' formattedValue={FMT.format('currencyShort', totals.fmv)} />
        <DataCard title='Active Lots' formattedValue={FMT.format('integer', totals.activeLotCount)} />
      </ul>
    );
  }

  // Tile order follows the design: ownership, FMV, shares, invested, unrealised G/L, multiple.
  return (
    <ul css={list}>
      <DataCard title='Current Ownership' formattedValue={FMT.format('percent', totals.ownershipPct)} />
      <DataCard title='FMV' formattedValue={FMT.format('currencyShort', totals.fmv)} />
      <DataCard title='Shares Outstanding' formattedValue={FMT.format('numericShort', totals.shares)} />
      <DataCard title='Total Invested' formattedValue={FMT.format('currencyShort', totals.invested)} />
      <DataCard
        title='Unrealized G/L'
        formattedValue={FMT.format('currencyShort', totals.unrealized)}
        valueAdornment={<ReturnChip returnPct={totals.returnPct} />}
      />
      <DataCard
        title='Current Position (Multiple)'
        formattedValue={FMT.format('multiplier', totals.multiple)}
      />
    </ul>
  );
}

/** The design shows the return beside the unrealised gain, tinted by direction. */
function ReturnChip({ returnPct }: { returnPct: number | null }) {
  if (returnPct === null) {
    return null;
  }
  const isGain = returnPct >= 0;

  return (
    <Typography
      variant='caption'
      component='span'
      css={returnChip}
      color={isGain ? colors.success[50] : colors.critical[50]}
    >
      {isGain ? <ArrowDropUpIcon fontSize='small' /> : <ArrowDropDownIcon fontSize='small' />}
      {FMT.format('percent', returnPct)} Return
    </Typography>
  );
}

interface IHoldingsTotals {
  shares: number;
  ownershipPct: number;
  invested: number;
  fmv: number;
  activeLotCount: number;
  unrealized: number;
  /** Percent units. `null` when nothing was invested, so there is no return to state. */
  returnPct: number | null;
  multiple: number;
}

function aggregate(holdings: OwnershipTimelineStack[]): IHoldingsTotals {
  const active = holdings.filter((stack) => stack.shares !== 0);
  const shares = active.reduce((s, stack) => s + stack.shares, 0);
  // ownershipPct is a fraction; the percent formatter expects percent units.
  const ownershipPct = active.reduce((s, stack) => s + (stack.ownershipPct ?? 0) * 100, 0);
  const invested = active.reduce((s, stack) => s + stack.cost, 0);
  const fmv = active.reduce((s, stack) => s + stack.fmv, 0);

  return {
    shares,
    ownershipPct,
    invested,
    fmv,
    activeLotCount: active.length,
    unrealized: fmv - invested,
    returnPct: invested === 0 ? null : ((fmv - invested) / invested) * 100,
    multiple: invested === 0 ? 0 : fmv / invested,
  };
}
